import type { Settings } from '@ais-ui/websockets';
import { MeasureToolUnits, Units } from '@ais-ui/utils';
import type { CoreSettings, DomainTranslator } from '../types';

const DEFAULTS: CoreSettings = {
  locale: 'en_US',
  timezone: 'Etc/UTC',
  date_format: 'd M Y',
  time_format: 'H:i',
  position_format: 'DM',
  units: { length: 'Nm' },
  theme: 'light',
  font_size: 12,
  auto_refresh: true,
  audio_alarms: false,
  export_utc: false,
  sidebar_expanded: true,
  table_density: 'normal',
  remote_control: false,
  favorite_port: null,
};

const migrateLegacyLengthUnit = (legacyUnit: string): string => {
  switch (legacyUnit) {
    case MeasureToolUnits.KILOMETERS:
      return Units.METRIC;
    case MeasureToolUnits.NAUTICAL_MILES:
      return Units.MARITIME;
    default:
      return Units.METRIC;
  }
};

export const coreTranslator: DomainTranslator<CoreSettings> = {
  fromBlob(blob: Settings): CoreSettings {
    const r = (blob['ribbonPlugin.settings'] ?? {}) as Record<string, unknown>;
    const top = blob as unknown as Record<string, unknown>;
    const pick = <V>(v: unknown, fallback: V): V =>
      v === undefined || v === null ? fallback : (v as V);

    // Derive length unit: use length_unit if present (pass-through), otherwise migrate legacy measure_tool_length_unit, otherwise fallback to default
    const lengthUnit = (() => {
      if (r.length_unit !== undefined && r.length_unit !== null) {
        return r.length_unit as string;
      }
      if (
        r.measure_tool_length_unit !== undefined &&
        r.measure_tool_length_unit !== null
      ) {
        return migrateLegacyLengthUnit(r.measure_tool_length_unit as string);
      }
      return DEFAULTS.units.length;
    })();

    return {
      locale: pick(r.locale, DEFAULTS.locale),
      timezone: pick(r.time_zone, DEFAULTS.timezone),
      date_format: pick(r.date_format_mp, DEFAULTS.date_format),
      time_format: pick(r.time_format, DEFAULTS.time_format),
      position_format: pick(r.pos_format, DEFAULTS.position_format),
      units: { length: lengthUnit },
      theme: pick(top.theme, DEFAULTS.theme),
      font_size: pick(r.font_size, DEFAULTS.font_size),
      auto_refresh: pick(r.auto_refresh_enabled, DEFAULTS.auto_refresh),
      audio_alarms: pick(r.enable_audio_alarm, DEFAULTS.audio_alarms),
      export_utc: pick(r.export_utc, DEFAULTS.export_utc),
      sidebar_expanded: pick(r.is_sidebar_expanded, DEFAULTS.sidebar_expanded),
      table_density: pick(r.table_density, DEFAULTS.table_density),
      remote_control: pick(r.remotecontrol_enabled, DEFAULTS.remote_control),
      favorite_port: pick(r.favorite_port, DEFAULTS.favorite_port),
    };
  },

  applyToBlob(blob: Settings, patch: Partial<CoreSettings>): Settings {
    const next = { ...(blob as unknown as Record<string, unknown>) };
    const ribbon = {
      ...((blob['ribbonPlugin.settings'] ?? {}) as Record<string, unknown>),
    };
    if (patch.locale !== undefined) ribbon.locale = patch.locale;
    if (patch.timezone !== undefined) ribbon.time_zone = patch.timezone;
    if (patch.date_format !== undefined)
      ribbon.date_format_mp = patch.date_format;
    if (patch.time_format !== undefined) ribbon.time_format = patch.time_format;
    if (patch.position_format !== undefined)
      ribbon.pos_format = patch.position_format;
    if (patch.units?.length !== undefined)
      ribbon.length_unit = patch.units.length;
    if (patch.font_size !== undefined) ribbon.font_size = patch.font_size;
    if (patch.auto_refresh !== undefined)
      ribbon.auto_refresh_enabled = patch.auto_refresh;
    if (patch.audio_alarms !== undefined)
      ribbon.enable_audio_alarm = patch.audio_alarms;
    if (patch.export_utc !== undefined) ribbon.export_utc = patch.export_utc;
    if (patch.sidebar_expanded !== undefined)
      ribbon.is_sidebar_expanded = patch.sidebar_expanded;
    if (patch.table_density !== undefined)
      ribbon.table_density = patch.table_density;
    if (patch.favorite_port !== undefined)
      ribbon.favorite_port = patch.favorite_port;
    if (patch.remote_control !== undefined)
      ribbon.remotecontrol_enabled = patch.remote_control;
    if (patch.theme !== undefined) next.theme = patch.theme;
    next['ribbonPlugin.settings'] = ribbon;
    return next as unknown as Settings;
  },
};
